import { useState } from 'react';
import {
  Plus, Loader2, Eye, EyeOff, Key, User, Globe, FileText, Clock,
  CreditCard, UserCircle, Mail, Phone, Building, MapPin, Hash, StickyNote,
  Trash2, ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { useVault } from './VaultContext';
import { CipherType, FieldType } from '@/api/vault';

interface AddItemSheetProps {
  collectionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Type labels and icons
const TYPE_OPTIONS = [
  { type: CipherType.Login, label: 'Логин', icon: Key, description: 'Логин и пароль для сайта или сервиса' },
  { type: CipherType.SecureNote, label: 'Заметка', icon: StickyNote, description: 'Защищённая текстовая заметка' },
  { type: CipherType.Card, label: 'Карта', icon: CreditCard, description: 'Банковская или платёжная карта' },
  { type: CipherType.Identity, label: 'Документ', icon: UserCircle, description: 'Личные данные и документы' },
];

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  [FieldType.Text]: 'Текст',
  [FieldType.Hidden]: 'Скрытое',
  [FieldType.Boolean]: 'Да/Нет',
  [FieldType.Linked]: 'Связанное',
};

export function AddItemSheet({ collectionId, open, onOpenChange }: AddItemSheetProps) {
  const { createItemInCollection } = useVault();

  // Step: 'type' for type selection, 'form' for data entry
  const [step, setStep] = useState<'type' | 'form'>('type');
  const [selectedType, setSelectedType] = useState<CipherType | null>(null);

  // Common fields
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');

  // Login fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [uris, setUris] = useState<string[]>(['']);
  const [showPassword, setShowPassword] = useState(false);

  // Card fields
  const [cardholderName, setCardholderName] = useState('');
  const [cardBrand, setCardBrand] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpMonth, setCardExpMonth] = useState('');
  const [cardExpYear, setCardExpYear] = useState('');
  const [cardCode, setCardCode] = useState('');
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [showCardCode, setShowCardCode] = useState(false);

  // Identity fields
  const [idTitle, setIdTitle] = useState('');
  const [idFirstName, setIdFirstName] = useState('');
  const [idMiddleName, setIdMiddleName] = useState('');
  const [idLastName, setIdLastName] = useState('');
  const [idCompany, setIdCompany] = useState('');
  const [idEmail, setIdEmail] = useState('');
  const [idPhone, setIdPhone] = useState('');
  const [idSsn, setIdSsn] = useState('');
  const [idPassportNumber, setIdPassportNumber] = useState('');
  const [idLicenseNumber, setIdLicenseNumber] = useState('');
  const [idAddress1, setIdAddress1] = useState('');
  const [idAddress2, setIdAddress2] = useState('');
  const [idCity, setIdCity] = useState('');
  const [idState, setIdState] = useState('');
  const [idPostalCode, setIdPostalCode] = useState('');
  const [idCountry, setIdCountry] = useState('');

  // Custom fields
  const [fields, setFields] = useState<{ type: FieldType; name: string; value: string }[]>([]);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [newFieldType, setNewFieldType] = useState<FieldType>(FieldType.Text);
  const [newFieldName, setNewFieldName] = useState('');
  const [showFieldValues, setShowFieldValues] = useState<Record<number, boolean>>({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setStep('type');
    setSelectedType(null);
    setName('');
    setNotes('');
    setUsername('');
    setPassword('');
    setTotp('');
    setUris(['']);
    setShowPassword(false);
    setCardholderName('');
    setCardBrand('');
    setCardNumber('');
    setCardExpMonth('');
    setCardExpYear('');
    setCardCode('');
    setShowCardNumber(false);
    setShowCardCode(false);
    setIdTitle('');
    setIdFirstName('');
    setIdMiddleName('');
    setIdLastName('');
    setIdCompany('');
    setIdEmail('');
    setIdPhone('');
    setIdSsn('');
    setIdPassportNumber('');
    setIdLicenseNumber('');
    setIdAddress1('');
    setIdAddress2('');
    setIdCity('');
    setIdState('');
    setIdPostalCode('');
    setIdCountry('');
    setFields([]);
    setFieldsOpen(false);
    setNewFieldName('');
    setShowFieldValues({});
  };

  const handleSelectType = (type: CipherType) => {
    setSelectedType(type);
    setStep('form');
  };

  const handleBack = () => {
    setStep('type');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedType || !name.trim()) {
      toast.error('Введите название');
      return;
    }

    setIsSubmitting(true);
    try {
      await createItemInCollection(collectionId, {
        type: selectedType,
        name: name.trim(),
        notes: notes.trim() || undefined,
        // Login
        username: selectedType === CipherType.Login ? username.trim() || undefined : undefined,
        password: selectedType === CipherType.Login ? password || undefined : undefined,
        totp: selectedType === CipherType.Login ? totp.trim() || undefined : undefined,
        uris: selectedType === CipherType.Login && uris.filter(u => u.trim()).length > 0
          ? uris.filter(u => u.trim())
          : undefined,
        // Card
        card: selectedType === CipherType.Card ? {
          cardholderName: cardholderName.trim() || undefined,
          brand: cardBrand.trim() || undefined,
          number: cardNumber.trim() || undefined,
          expMonth: cardExpMonth.trim() || undefined,
          expYear: cardExpYear.trim() || undefined,
          code: cardCode.trim() || undefined,
        } : undefined,
        // Identity
        identity: selectedType === CipherType.Identity ? {
          title: idTitle.trim() || undefined,
          firstName: idFirstName.trim() || undefined,
          middleName: idMiddleName.trim() || undefined,
          lastName: idLastName.trim() || undefined,
          company: idCompany.trim() || undefined,
          email: idEmail.trim() || undefined,
          phone: idPhone.trim() || undefined,
          ssn: idSsn.trim() || undefined,
          passportNumber: idPassportNumber.trim() || undefined,
          licenseNumber: idLicenseNumber.trim() || undefined,
          address1: idAddress1.trim() || undefined,
          address2: idAddress2.trim() || undefined,
          city: idCity.trim() || undefined,
          state: idState.trim() || undefined,
          postalCode: idPostalCode.trim() || undefined,
          country: idCountry.trim() || undefined,
        } : undefined,
        // Custom fields
        fields: fields.length > 0 ? fields.filter(f => f.name.trim()) : undefined,
      });

      toast.success('Запись добавлена');
      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast.error('Ошибка', {
        description: error instanceof Error ? error.message : 'Не удалось создать запись',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  // URI handlers
  const addUri = () => setUris([...uris, '']);
  const removeUri = (index: number) => setUris(uris.filter((_, i) => i !== index));
  const updateUri = (index: number, value: string) => {
    const newUris = [...uris];
    newUris[index] = value;
    setUris(newUris);
  };

  // Custom field handlers
  const addField = () => {
    if (!newFieldName.trim()) return;
    setFields([...fields, { type: newFieldType, name: newFieldName.trim(), value: '' }]);
    setNewFieldName('');
    setNewFieldType(FieldType.Text);
  };
  const removeField = (index: number) => setFields(fields.filter((_, i) => i !== index));
  const updateField = (index: number, key: 'name' | 'value', value: string) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], [key]: value };
    setFields(newFields);
  };

  const typeInfo = selectedType !== null ? TYPE_OPTIONS.find(t => t.type === selectedType) : null;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            {step === 'form' && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 mr-1"
                onClick={handleBack}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            {step === 'type' ? (
              <>
                <Plus className="h-5 w-5" />
                Новая запись
              </>
            ) : typeInfo ? (
              <>
                <typeInfo.icon className="h-5 w-5" />
                Новый {typeInfo.label.toLowerCase()}
              </>
            ) : null}
          </SheetTitle>
          <SheetDescription>
            {step === 'type' ? 'Выберите тип записи' : typeInfo?.description}
          </SheetDescription>
        </SheetHeader>

        {/* Step 1: Type Selection */}
        {step === 'type' && (
          <div className="flex-1 overflow-y-auto mt-4 space-y-3">
            {TYPE_OPTIONS.map((option) => (
              <button
                key={option.type}
                type="button"
                className="w-full flex items-center gap-4 p-4 rounded-lg border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors text-left"
                onClick={() => handleSelectType(option.type)}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <option.icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{option.label}</div>
                  <div className="text-sm text-muted-foreground truncate">{option.description}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Form */}
        {step === 'form' && selectedType !== null && (
          <>
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto mt-4 space-y-4 pb-4">
              {/* Common: Name */}
              <div className="space-y-2">
                <Label htmlFor="item-name">Название *</Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="item-name"
                    placeholder="Название записи"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    disabled={isSubmitting}
                    autoFocus
                  />
                </div>
              </div>

              {/* Login Fields */}
              {selectedType === CipherType.Login && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="item-username">Логин</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="item-username"
                        placeholder="user@example.com"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="pl-10"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="item-password">Пароль</Label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="item-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Пароль"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10"
                        disabled={isSubmitting}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="item-totp">TOTP Secret</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="item-totp"
                        placeholder="otpauth:// или base32 секрет"
                        value={totp}
                        onChange={(e) => setTotp(e.target.value)}
                        className="pl-10"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>URL адреса</Label>
                    {uris.map((uri, index) => (
                      <div key={index} className="flex gap-2">
                        <div className="relative flex-1">
                          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="url"
                            placeholder="https://example.com"
                            value={uri}
                            onChange={(e) => updateUri(index, e.target.value)}
                            className="pl-10"
                            disabled={isSubmitting}
                          />
                        </div>
                        {uris.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeUri(index)}
                            disabled={isSubmitting}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addUri}
                      disabled={isSubmitting}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Добавить URL
                    </Button>
                  </div>
                </>
              )}

              {/* SecureNote Fields - just notes (below) */}

              {/* Card Fields */}
              {selectedType === CipherType.Card && (
                <>
                  <div className="space-y-2">
                    <Label>Владелец карты</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Имя на карте"
                        value={cardholderName}
                        onChange={(e) => setCardholderName(e.target.value)}
                        className="pl-10"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Бренд</Label>
                    <Select value={cardBrand} onValueChange={setCardBrand} disabled={isSubmitting}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите бренд" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Visa">Visa</SelectItem>
                        <SelectItem value="Mastercard">Mastercard</SelectItem>
                        <SelectItem value="Amex">American Express</SelectItem>
                        <SelectItem value="Mir">МИР</SelectItem>
                        <SelectItem value="Other">Другой</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Номер карты</Label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type={showCardNumber ? 'text' : 'password'}
                        placeholder="1234 5678 9012 3456"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        className="pl-10 pr-10"
                        disabled={isSubmitting}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                        onClick={() => setShowCardNumber(!showCardNumber)}
                      >
                        {showCardNumber ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-2">
                      <Label>Месяц</Label>
                      <Input
                        placeholder="MM"
                        value={cardExpMonth}
                        onChange={(e) => setCardExpMonth(e.target.value)}
                        maxLength={2}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Год</Label>
                      <Input
                        placeholder="YYYY"
                        value={cardExpYear}
                        onChange={(e) => setCardExpYear(e.target.value)}
                        maxLength={4}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CVV</Label>
                      <div className="relative">
                        <Input
                          type={showCardCode ? 'text' : 'password'}
                          placeholder="***"
                          value={cardCode}
                          onChange={(e) => setCardCode(e.target.value)}
                          maxLength={4}
                          className="pr-10"
                          disabled={isSubmitting}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-8"
                          onClick={() => setShowCardCode(!showCardCode)}
                        >
                          {showCardCode ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Identity Fields */}
              {selectedType === CipherType.Identity && (
                <>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="space-y-2">
                      <Label>Обращение</Label>
                      <Select value={idTitle} onValueChange={setIdTitle} disabled={isSubmitting}>
                        <SelectTrigger>
                          <SelectValue placeholder="-" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">-</SelectItem>
                          <SelectItem value="Mr">Г-н</SelectItem>
                          <SelectItem value="Mrs">Г-жа</SelectItem>
                          <SelectItem value="Ms">Мисс</SelectItem>
                          <SelectItem value="Dr">Доктор</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3 space-y-2">
                      <Label>Имя</Label>
                      <Input
                        placeholder="Имя"
                        value={idFirstName}
                        onChange={(e) => setIdFirstName(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label>Отчество</Label>
                      <Input
                        placeholder="Отчество"
                        value={idMiddleName}
                        onChange={(e) => setIdMiddleName(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Фамилия</Label>
                      <Input
                        placeholder="Фамилия"
                        value={idLastName}
                        onChange={(e) => setIdLastName(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="email@example.com"
                          value={idEmail}
                          onChange={(e) => setIdEmail(e.target.value)}
                          className="pl-10"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Телефон</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="tel"
                          placeholder="+375..."
                          value={idPhone}
                          onChange={(e) => setIdPhone(e.target.value)}
                          className="pl-10"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Компания</Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Название компании"
                        value={idCompany}
                        onChange={(e) => setIdCompany(e.target.value)}
                        className="pl-10"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Адрес</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Улица, дом"
                        value={idAddress1}
                        onChange={(e) => setIdAddress1(e.target.value)}
                        className="pl-10 mb-2"
                        disabled={isSubmitting}
                      />
                    </div>
                    <Input
                      placeholder="Квартира, офис"
                      value={idAddress2}
                      onChange={(e) => setIdAddress2(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label>Город</Label>
                      <Input
                        placeholder="Город"
                        value={idCity}
                        onChange={(e) => setIdCity(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Область</Label>
                      <Input
                        placeholder="Область/Регион"
                        value={idState}
                        onChange={(e) => setIdState(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label>Индекс</Label>
                      <Input
                        placeholder="220000"
                        value={idPostalCode}
                        onChange={(e) => setIdPostalCode(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Страна</Label>
                      <Input
                        placeholder="Беларусь"
                        value={idCountry}
                        onChange={(e) => setIdCountry(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="space-y-2 pt-2 border-t">
                    <Label className="text-muted-foreground">Документы</Label>
                    <div className="grid grid-cols-1 gap-2">
                      <div className="relative">
                        <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="password"
                          placeholder="ИНН / SSN"
                          value={idSsn}
                          onChange={(e) => setIdSsn(e.target.value)}
                          className="pl-10"
                          disabled={isSubmitting}
                        />
                      </div>
                      <Input
                        type="password"
                        placeholder="Номер паспорта"
                        value={idPassportNumber}
                        onChange={(e) => setIdPassportNumber(e.target.value)}
                        disabled={isSubmitting}
                      />
                      <Input
                        type="password"
                        placeholder="Номер вод. удостоверения"
                        value={idLicenseNumber}
                        onChange={(e) => setIdLicenseNumber(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Notes (all types) */}
              <div className="space-y-2">
                <Label htmlFor="item-notes">Заметки</Label>
                <Textarea
                  id="item-notes"
                  placeholder="Дополнительные заметки..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={selectedType === CipherType.SecureNote ? 12 : 4}
                  className={selectedType === CipherType.SecureNote ? 'min-h-[200px] resize-y' : 'resize-y'}
                  disabled={isSubmitting}
                />
              </div>

              {/* Custom Fields */}
              <Collapsible open={fieldsOpen} onOpenChange={setFieldsOpen}>
                <CollapsibleTrigger
                  type="button"
                  className="flex items-center justify-center w-full h-9 px-3 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent hover:text-accent-foreground"
                >
                  {fieldsOpen ? 'Скрыть' : 'Показать'} дополнительные поля ({fields.length})
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 mt-2">
                  {fields.map((field, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        placeholder="Название"
                        value={field.name}
                        onChange={(e) => updateField(index, 'name', e.target.value)}
                        className="flex-1"
                        disabled={isSubmitting}
                      />
                      <div className="relative flex-1">
                        <Input
                          type={field.type === FieldType.Hidden && !showFieldValues[index] ? 'password' : 'text'}
                          placeholder="Значение"
                          value={field.value}
                          onChange={(e) => updateField(index, 'value', e.target.value)}
                          className={field.type === FieldType.Hidden ? 'pr-10' : ''}
                          disabled={isSubmitting}
                        />
                        {field.type === FieldType.Hidden && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-8"
                            onClick={() => setShowFieldValues({ ...showFieldValues, [index]: !showFieldValues[index] })}
                          >
                            {showFieldValues[index] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                          </Button>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeField(index)}
                        disabled={isSubmitting}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}

                  <div className="flex gap-2 pt-2 border-t">
                    <Select
                      value={String(newFieldType)}
                      onValueChange={(v) => setNewFieldType(Number(v) as FieldType)}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={String(FieldType.Text)}>{FIELD_TYPE_LABELS[FieldType.Text]}</SelectItem>
                        <SelectItem value={String(FieldType.Hidden)}>{FIELD_TYPE_LABELS[FieldType.Hidden]}</SelectItem>
                        <SelectItem value={String(FieldType.Boolean)}>{FIELD_TYPE_LABELS[FieldType.Boolean]}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Название поля"
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      className="flex-1"
                      disabled={isSubmitting}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={addField}
                      disabled={isSubmitting || !newFieldName.trim()}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </form>

            {/* Submit button */}
            <div className="flex-shrink-0 pt-4 border-t">
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || !name.trim()}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Сохранение...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Сохранить
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
